/**
 * Where in the open document a source of an answer is: the Word paragraph a
 * `[n]` points at. No Word and no React in here; the pane reads the document's
 * paragraphs and hands them in.
 *
 * The open document reaches the agent as its text, one line to a paragraph
 * (`DocumentReader.getFullDocumentContext`), and the agent's index numbers those
 * lines (engine `ask_agent/corpus.py`, `read_text`): "פסקה 11" is the 11th line.
 * The document may have changed since the answer was given, so the paragraph is
 * found by the words the source quotes; the number says which of several places
 * that hold the same words is meant.
 */

/** A paragraph of the open document, by its index in Word's body. */
export type DocumentParagraph = { index: number; text: string };

/** What a source says of where it is. */
export type CitedPlace = { location?: string; quote?: string; fullText?: string };

const MARKS = /[\u0591-\u05c7\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff]/g;
const FINALS: Record<string, string> = { ך: 'כ', ם: 'מ', ן: 'נ', ף: 'פ', ץ: 'צ' };
const GROUPED = /(\d)[,\u00a0\u202f](?=\d{3}(?!\d))/g;
const SQUEEZED = /[\s"'`“”‘’.,;:()[\]|-]+/g;
/** The line breaks Python's `str.splitlines` breaks at: the engine cuts the document's text into lines with it. */
const LINE_BREAKS = /\r\n|[\n\r\v\f\x1c\x1d\x1e\x85\u2028\u2029]/;

/** Text as the engine's quote check compares it (`ask_agent/agent.py`, `_squeeze`): folded, with no spaces or punctuation. */
export function squeeze(text: string): string {
  const cleaned = (text || '')
    .normalize('NFKC')
    .replace(MARKS, '')
    .replace(/״/g, '"')
    .replace(/׳/g, "'")
    .replace(/[\u05be–—]/g, '-');
  const folded = cleaned
    .toLowerCase()
    .replace(/[ךםןףץ]/g, (letter) => FINALS[letter])
    .replace(GROUPED, '$1');
  return folded.replace(SQUEEZED, '');
}

/** The number in "פסקה 11"; null for any other place (a page, a sheet's row, a table's row). */
export function paragraphNumber(location: string | undefined): number | null {
  const found = /^\s*פסקה\s+(\d+)\s*$/.exec(location || '');
  return found ? Number(found[1]) : null;
}

/** The document's lines as the engine numbered them, each with the Word paragraph it came from. */
function documentLines(paragraphs: DocumentParagraph[]): Array<{ paragraph: number; text: string }> {
  const lines: Array<{ paragraph: number; text: string }> = [];
  for (const paragraph of paragraphs) {
    for (const line of paragraph.text.split(LINE_BREAKS)) {
      if (line.trim()) lines.push({ paragraph: paragraph.index, text: line });
    }
  }
  return lines;
}

/**
 * The index in Word's body of the paragraph a source points at; null when its
 * words are no longer in the document. Of several places that hold the quoted
 * words, the one nearest the numbered paragraph is taken.
 */
export function findCitedParagraph(paragraphs: DocumentParagraph[], place: CitedPlace): number | null {
  const lines = documentLines(paragraphs);
  const numbered = paragraphNumber(place.location);
  const expected = numbered !== null && numbered >= 1 && numbered <= lines.length ? numbered - 1 : null;
  const wanted = squeeze(place.quote || '') || squeeze(place.fullText || '');
  if (!wanted) return expected !== null ? lines[expected].paragraph : null;

  // The lines one after another, as the quote check sees them: a quote may run over more than one.
  const starts: number[] = [];
  let joined = '';
  for (const line of lines) {
    starts.push(joined.length);
    joined += squeeze(line.text);
  }
  const lineAt = (offset: number): number => {
    let low = 0;
    let high = starts.length - 1;
    while (low < high) {
      const middle = (low + high + 1) >> 1;
      if (starts[middle] <= offset) low = middle;
      else high = middle - 1;
    }
    return low;
  };

  let best: number | null = null;
  for (let at = joined.indexOf(wanted); at !== -1; at = joined.indexOf(wanted, at + 1)) {
    const line = lineAt(at);
    if (expected === null) return lines[line].paragraph;
    if (best === null || Math.abs(line - expected) < Math.abs(best - expected)) best = line;
    if (best === expected) break;
  }
  return best !== null ? lines[best].paragraph : null;
}
